/**
 * Interface representing a link in the agent's web search settings
 */
export interface AgentLink {
  /**
   * Unique ID for the link
   */
  id: string;

  /**
   * Title of the link
   */
  title: string;

  /**
   * URL of the link
   */
  url: string;

  /**
   * General description of what the link is about
   */
  description: string | null;

  /**
   * Specific instructions for how the AI should use this link
   */
  ai_instructions: string | null;

  /**
   * Timestamp of when the link was created
   */
  created_at: string;
}

/**
 * Interface representing web search settings for agents
 */
export interface WebSearchSettings {
  /**
   * Whether Google search is enabled
   */
  google_search: boolean;
  
  /**
   * Whether searching on the company website is enabled
   */
  company_website: boolean;
  
  /**
   * Array of website URLs the agent can search on
   * @deprecated Use the links array instead
   */
  selected_links?: string[];

  /**
   * Array of link objects the agent can search on
   */
  links: AgentLink[];
  
  /**
   * Custom instructions for how the AI should use the web search tool
   */
  web_search_instructions?: string;
}

/**
 * Generate instruction text based on web search settings
 * @deprecated This has been moved to src/utils/webSearchUtils.ts
 */
// The implementation has been moved to src/utils/webSearchUtils.ts to avoid duplication

/**
 * Convert the legacy string format to the new WebSearchSettings format
 */
export function convertLegacyWebSearchSetting(legacySetting: string): WebSearchSettings {
  return {
    google_search: legacySetting === "enabled",
    company_website: false,
    links: [],
    selected_links: []
  };
}

/**
 * Check if any web search capability is enabled
 */
export function isWebSearchEnabled(settings: WebSearchSettings | string | null | undefined): boolean {
  if (!settings) return false;
  
  // Handle legacy string format
  if (typeof settings === "string") {
    return settings === "enabled";
  }
  
  // Handle new object format
  return settings.google_search || 
         settings.company_website || 
         (settings.links && settings.links.length > 0) ||
         (settings.selected_links && settings.selected_links.length > 0);
} 